import { Fragment, useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Row, Col, Card, Nav, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { getCompanyInfo } from "../apis/CompanyInfo";
import useDarkMode from "../hooks/useDarkMode";
import {AdUnit} from "../components/AdComponent";
import KeyMetricsSection from "./sections/KeyMetricsSection";
import StockChartSection from "./sections/StockChartSection";
import InvestorListSection from "./sections/InvestorListSection";
import InsiderListSection from "./sections/InsiderListSection";

const FMP_BATCH_QUOTES_URL = "https://financialmodelingprep.com/stable/batch-quote";
const isScrollableElement = (element) => {
  if (!element || !(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;
  return (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") && element.scrollHeight > element.clientHeight;
};

const findScrollParent = (element) => {
  let current = element?.parentElement;

  while (current) {
    if (isScrollableElement(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return window;
};

export default function Stock({ ticker: propTicker, embedded = false }) {
  const { ticker: urlTicker } = useParams();
  const ticker = propTicker || urlTicker;

  const [companyInfo, setCompanyInfo] = useState(null);
  const [batchQuotes, setBatchQuotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMainTab, setActiveMainTab] = useState("13f");
  const [chartSelection, setChartSelection] = useState(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const scrollTargetRef = useRef(window);
  const rafRef = useRef(null);

  const isDarkMode = useDarkMode();
  const borderColor = isDarkMode ? "rgba(255,255,255,0.1)" : "#dee2e6";

  const stickyTop = embedded ? 0 : 72;
  const stickyOnThreshold = embedded ? 0 : 64;
  const stickyOffThreshold = embedded ? 12 : 88;

  const measureStickyHeader = useCallback(() => {
    if (!stickyHeaderRef.current) return;
    const nextHeight = stickyHeaderRef.current.offsetHeight || 0;
    setStickyHeaderHeight((prev) => (prev !== nextHeight ? nextHeight : prev));
  }, []);

  const getHeaderTopOffset = useCallback(() => {
    if (!headerRef.current) return Number.POSITIVE_INFINITY;

    const headerRect = headerRef.current.getBoundingClientRect();

    if (embedded) {
      const scrollParent = findScrollParent(headerRef.current);

      if (scrollParent !== window) {
        const parentRect = scrollParent.getBoundingClientRect();
        return headerRect.top - parentRect.top;
      }
    }

    return headerRect.top;
  }, [embedded]);

  const updateStickyHeader = useCallback(() => {
    if (!headerRef.current) {
      setShowStickyHeader(false);
      return;
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const topOffset = getHeaderTopOffset();

      setShowStickyHeader((prev) => {
        if (!prev && topOffset <= stickyOnThreshold) return true;
        if (prev && topOffset >= stickyOffThreshold) return false;
        return prev;
      });
    });
  }, [getHeaderTopOffset, stickyOnThreshold, stickyOffThreshold]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await getCompanyInfo(ticker);
        if (!response.ok) {
          throw new Error("Failed to fetch company info");
        }
        const data = await response.json();
        setCompanyInfo(data.data[0]);
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
    };

    const fetchBatchQuotes = async () => {
      try {
        const url = `${FMP_BATCH_QUOTES_URL}?symbols=${ticker}&apikey=${process.env.REACT_APP_FINANCIAL_API_KEY}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Failed to fetch batch quotes");
        }
        const data = await response.json();
        setBatchQuotes(data[0]);
      } catch (error) {
        console.error("Error fetching batch quotes:", error);
      }
    };

    if (ticker) {
      setLoading(true);
      Promise.all([fetchCompanyInfo(), fetchBatchQuotes()])
        .finally(() => setLoading(false));
    }
  }, [ticker]);

  useEffect(() => {
    const nextScrollTarget =
      embedded && headerRef.current ? findScrollParent(headerRef.current) : window;

    scrollTargetRef.current = nextScrollTarget;
    updateStickyHeader();

    nextScrollTarget.addEventListener("scroll", updateStickyHeader, { passive: true });

    return () => {
      nextScrollTarget.removeEventListener("scroll", updateStickyHeader);
    };
  }, [embedded, ticker, updateStickyHeader]);

  useLayoutEffect(() => {
    measureStickyHeader();
  }, [measureStickyHeader, isMobile, companyInfo, batchQuotes]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      measureStickyHeader();
      updateStickyHeader();
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [measureStickyHeader, updateStickyHeader]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const price = batchQuotes?.price;
  const change = batchQuotes?.change > 0 ? `+${batchQuotes?.change}` : batchQuotes?.change;
  const changePercentage =
    Number(batchQuotes?.changePercentage) > 0 ?
      `+${Number(batchQuotes?.changePercentage).toFixed(2)}` :
      Number(batchQuotes?.changePercentage).toFixed(2);

  if (loading || !companyInfo) {
    return (
      <div className={`d-flex flex-column align-items-center justify-content-center ${embedded ? "py-5" : "vh-100"} text-muted`}>
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: "2rem", height: "2rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "14px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid w-100 mx-auto">
        <div
          style={{
            height: showStickyHeader ? stickyHeaderHeight : 0,
            transition: "height 0.15s ease",
          }}
        />
        {/* 1. Header Section */}

          <div
            ref={stickyHeaderRef}
            className="position-sticky pt-2 mb-2"
            style={{
              top: stickyTop,
              zIndex: 700,
              opacity: showStickyHeader ? 1 : 0,
              visibility: showStickyHeader ? "visible" : "hidden",
              display: showStickyHeader ? "block" : "none",
              pointerEvents: showStickyHeader ? "auto" : "none",
              transition: "opacity 0.15s ease, visibility 0.15s ease",
            }}
          >
            <Card className="border-0 shadow-sm">
              <Card.Body className="py-2">
                <div className="d-flex align-items-center gap-2">
                  <h2 className="m-0 text-dark text-truncate" style={{ minWidth: 0, wordBreak: "keep-all", fontWeight: 600, fontSize: isMobile ? "1.55rem" : "1.75rem", flex: 1 }}>
                    {companyInfo?.companykor} <span className="text-secondary" style={{ fontWeight: 500, fontSize: isMobile ? "1rem" : "1.05rem" }}>{companyInfo?.ticker}</span>
                  </h2>
                  <div className={`fs-5 text-${batchQuotes?.change > 0 ? 'danger' : 'primary'} lh-sm flex-shrink-0`} style={{ fontWeight: 600 }}>
                    {price} {change} ({changePercentage}%)
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
       
        <Row className="mb-2">
          <Col xs={12} className="mt-2" ref={headerRef}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                  <h2 className="m-0 text-dark flex-shrink-0" style={{ width: "fit-content", wordBreak: "keep-all", fontWeight: 600 }}>
                    {companyInfo?.companykor} <span className="text-secondary" style={{ fontWeight: 500, fontSize: "1.1rem" }}>{companyInfo?.ticker}</span>
                  </h2>
                  <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 gap-lg-3 flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex flex-column justify-content-center flex-shrink-0">
                      <div className={`fs-5 text-${batchQuotes?.change > 0 ? 'danger' : 'primary'} lh-sm`} style={{ fontWeight: 600 }}>
                        {price} {change} ({changePercentage}%)
                      </div>
                      <div className="text-secondary lh-sm mt-1" style={{ fontWeight: 500 }}>
                        {companyInfo?.exchange} / {companyInfo?.sector}
                      </div>
                    </div>
                    <div className="text-dark flex-grow-1" style={{ fontWeight: 500, minWidth: 0 }}>
                      {companyInfo?.intro}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* 2. Key Metrics */}
        <KeyMetricsSection ticker={ticker} />
        <Row className="g-2">
          {/* 3. 탭 영역 (Desktop: Left Bottom, Mobile: 3rd) */}
          <Col xs={12} lg={12}>
            <div className="h-100 px-1" style={{ overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap", WebkitOverflowScrolling: "touch" }}>
              <Nav
                className="nav-line mb-3 flex-nowrap"
                variant="underline"
                activeKey={activeMainTab}
                onSelect={(k) => setActiveMainTab(k)}
                style={{ borderBottom: `1px solid ${borderColor}`, minWidth: "max-content" }}
              >
                <Nav.Link eventKey="13f" style={{ whiteSpace: "nowrap" }}>13F</Nav.Link>
                <Nav.Link eventKey="insider" style={{ whiteSpace: "nowrap" }}>내부자</Nav.Link>
                <Nav.Link eventKey="income" style={{ whiteSpace: "nowrap" }}>
                  손익계산서 <Badge bg="danger" pill className="ms-1" style={{ fontSize: "0.7em", verticalAlign: "center" }}>Beta</Badge>
                </Nav.Link>
                <Nav.Link eventKey="balance" style={{ whiteSpace: "nowrap" }}>
                  재무상태표 <Badge bg="danger" pill className="ms-1" style={{ fontSize: "0.7em", verticalAlign: "center" }}>Beta</Badge>
                </Nav.Link>
                <Nav.Link eventKey="cashflow" style={{ whiteSpace: "nowrap" }}>
                  현금흐름표 <Badge bg="danger" pill className="ms-1" style={{ fontSize: "0.7em", verticalAlign: "center" }}>Beta</Badge>
                </Nav.Link>
              </Nav>
            </div>
          </Col>
        </Row>
        <Row className="g-2">
          {/* 1. 주가차트 (Desktop: Left Top, Mobile: 1st) */}
          {!(isMobile && activeMainTab === "13f") && (
            <Col xs={{ offset: 12, order: 2 }} sm={{ offset: 12, order: 2 }} md={{ offset: 12, order: 2 }} lg={["income", "balance", "cashflow"].includes(activeMainTab) ? 12 : 9}>
              <StockChartSection ticker={ticker} companykor={companyInfo?.companykor} chartSelection={chartSelection} activeMainTab={activeMainTab} />
            </Col>
          )}
          {/* 2. 투자자 리스트 (Desktop: Right Top, Mobile: 2nd) */}
          {(!["income", "balance", "cashflow"].includes(activeMainTab)) && (
            <Col xs={{ offset: 12, order: 1 }} sm={{ offset: 12, order: 1 }} md={{ offset: 12, order: 1 }} lg={3} className="order-1 order-lg-2" >
              {activeMainTab === "insider" ? (
                <InsiderListSection
                  ticker={ticker}
                  companykor={companyInfo?.companykor}
                  activeMainTab={activeMainTab}
                  onSelectionChange={setChartSelection}
                  disableNavigate={isMobile && activeMainTab === "13f"}
                />
              ) : (
                <InvestorListSection
                  ticker={ticker}
                  companykor={companyInfo?.companykor}
                  activeMainTab={activeMainTab}
                  onSelectionChange={setChartSelection}
                  disableNavigate={isMobile && activeMainTab === "13f"}
                />
              )}
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            {activeMainTab !== "income" && (
              <div className="d-flex justify-content-center my-4">
                <div style={{ width: '100%', maxWidth: '970px' }}>
                  <AdUnit key="9172518321" slot="9172518321" style={{ overflowX: "scroll", overflowY: "hidden" }} />
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>

    </Fragment>
  );
}
